import { FC, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Checkbox, Drawer, InputNumber, Progress, Segmented, Switch, Tabs } from 'antd';
import { GroupedVirtuoso } from 'react-virtuoso';
import {
  BimClashFocusMode,
  BimClashItem,
  BimClashItemRef,
  BimClashMode,
  BimViewerHandle
} from 'Data/interfaces/BimViewer/IBimViewerPort';
import { CLASH_PRESETS, CUSTOM_RULE_ID } from '../../clashRules';
import { ClashTab, IClashModel, useClashPanel } from './useClashPanel';
import ClashTypePicker from './ClashTypePicker';
import stylesDrawer from '../PropertiesDrawer/PropertiesDrawer.module.scss';
import {
  Actions,
  Body,
  ClashRow,
  Content,
  Field,
  Hint,
  IssueHeader,
  ModelRow,
  RuleRow,
  Summary
} from './styles';

export const CLASH_DRAWER_WIDTH = 360;
const DRAWER_HEIGHT = 541;
const RESULTS_LIST_HEIGHT = 300;

interface IClashPanelProps {
  open: boolean;
  handleRef: MutableRefObject<BimViewerHandle | null>;
  /** Modelos ligados na cena, com o nome do arquivo. */
  models: IClashModel[];
  onClose(): void;
}

const toMm = (meters: number): number => Math.round(Math.abs(meters) * 1000);

/**
 * Verificação de interferências no federado, sobre os modelos na cena. Três
 * abas: verificar (modo, valor, escopo), regras por tipo e resultados. A
 * geometria é testada no motor; aqui só se monta a regra e se mostra o resultado.
 */
const ClashPanel: FC<IClashPanelProps> = ({ open, handleRef, models, onClose }) => {
  const { t } = useTranslation('federatedViewer');
  const panel = useClashPanel({ open, handleRef, models });
  const { result } = panel;

  const sideLabel = (side: BimClashItemRef) => (
    <div className="side">
      {side.name || t('clash.unnamed')} <span className="type">{side.ifcType}</span>
    </div>
  );

  const distanceLabel = (clash: BimClashItem) => (clash.status === 'clearance'
    ? t('clash.gap', { mm: toMm(clash.distance) })
    : t('clash.depth', { mm: toMm(clash.distance) }));

  const progressPercent = panel.progress && panel.progress.total > 0
    ? Math.round((panel.progress.done / panel.progress.total) * 100)
    : 0;

  const runTab = (
    <Body>
      <div>
        <label>
          {t('clash.models', { selected: panel.selectedUrns.length, total: panel.models.length })}
        </label>
        {panel.models.map((model) => (
          <ModelRow key={model.urn} title={model.name}>
            <Checkbox
              checked={panel.selectedUrns.includes(model.urn)}
              onChange={(event) => panel.toggleModel(model.urn, event.target.checked)}
            >
              {model.name}
            </Checkbox>
          </ModelRow>
        ))}
        {panel.selectedUrns.length === 0 && <Hint>{t('clash.noModelSelected')}</Hint>}
      </div>
      <Segmented
        block
        size="small"
        value={panel.mode}
        onChange={(mode) => panel.setMode(mode as BimClashMode)}
        options={[
          { value: 'hard', label: t('clash.modeHard') },
          { value: 'clearance', label: t('clash.modeClearance') }
        ]}
      />
      <div>
        <label>{panel.mode === 'hard' ? t('clash.tolerance') : t('clash.clearance')}</label>
        <InputNumber
          size="small"
          min={0}
          addonAfter="mm"
          value={panel.mode === 'hard' ? panel.toleranceMm : panel.clearanceMm}
          onChange={(value) => (panel.mode === 'hard'
            ? panel.setToleranceMm(value ?? 0)
            : panel.setClearanceMm(value ?? 0))}
        />
      </div>
      <Field>
        <span>{t('clash.crossModelOnly')}</span>
        <Switch
          size="small"
          checked={panel.crossModelOnly}
          disabled={panel.selectedUrns.length < 2}
          onChange={panel.setCrossModelOnly}
        />
      </Field>
      <Field>
        <span>{t('clash.activeRules', { count: panel.activeRuleCount })}</span>
        <Button type="link" size="small" onClick={() => panel.setTab('rules')}>
          {t('clash.editRules')}
        </Button>
      </Field>

      {panel.runState === 'running' ? (
        <div>
          <Progress percent={progressPercent} size="small" status="active" />
          <Button block size="small" onClick={panel.cancel}>{t('clash.cancel')}</Button>
        </div>
      ) : (
        <Button type="primary" block disabled={!panel.canRun} onClick={panel.run}>
          {t('clash.run')}
        </Button>
      )}
      {panel.runState === 'unsupported' && <Alert type="warning" showIcon message={t('clash.unsupported')} />}
      {panel.runState === 'error' && <Alert type="error" showIcon message={t('clash.error')} />}
    </Body>
  );

  const rulesTab = (
    <Body>
      {CLASH_PRESETS.map((preset) => (
        <RuleRow key={preset.id}>
          <Switch
            size="small"
            checked={panel.enabledRules.has(preset.id)}
            onChange={(checked) => panel.toggleRule(preset.id, checked)}
          />
          <div className="ruleText">
            <div className="ruleName">{panel.nameOf(preset.id)}</div>
            <div className="ruleTypes" title={preset.a && `${preset.a} × ${preset.b}`}>
              {preset.a ? `${preset.a} × ${preset.b}` : t('clash.allElements')}
            </div>
          </div>
        </RuleRow>
      ))}
      <RuleRow>
        <Switch
          size="small"
          checked={panel.enabledRules.has(CUSTOM_RULE_ID)}
          onChange={(checked) => panel.toggleRule(CUSTOM_RULE_ID, checked)}
        />
        <div className="ruleText">
          <div className="ruleName">{panel.nameOf(CUSTOM_RULE_ID)}</div>
          <div className="ruleTypes">{t('clash.customHint')}</div>
        </div>
      </RuleRow>
      {panel.enabledRules.has(CUSTOM_RULE_ID) && (
        <>
          <ClashTypePicker
            label={t('clash.sideA')}
            types={panel.types}
            selected={panel.custom.typesA}
            onChange={(typesA) => panel.setCustom({ ...panel.custom, typesA })}
          />
          <ClashTypePicker
            label={t('clash.sideB')}
            types={panel.types}
            selected={panel.custom.typesB}
            onChange={(typesB) => panel.setCustom({ ...panel.custom, typesB })}
          />
        </>
      )}
    </Body>
  );

  const resultsTab = !result ? (
    <Body><Hint>{t('clash.noResult')}</Hint></Body>
  ) : (
    <Body>
      <Summary>
        <span className="count">{result.clashes.length}</span>
        <span>
          {t('clash.summary', {
            issues: result.issueCount,
            elements: result.elementCount,
            seconds: (result.elapsedMs / 1000).toFixed(1)
          })}
        </span>
      </Summary>
      {panel.stale && <Alert type="info" showIcon message={t('clash.stale')} />}
      {result.truncated && <Hint>{t('clash.truncated')}</Hint>}

      <Actions>
        <Button
          size="small"
          type={panel.highlightAll ? 'primary' : 'default'}
          onClick={() => panel.setHighlightAll(!panel.highlightAll)}
        >
          {t('clash.highlightAll')}
        </Button>
        <Button size="small" type="link" onClick={panel.clear}>{t('clash.clear')}</Button>
      </Actions>
      <div>
        <label>{t('clash.onClick')}</label>
        <Segmented
          block
          size="small"
          value={panel.focusMode}
          onChange={(mode) => panel.setFocusMode(mode as BimClashFocusMode)}
          options={[
            { value: 'highlight', label: t('clash.focusHighlight') },
            { value: 'isolate', label: t('clash.focusIsolate') },
            { value: 'ghost', label: t('clash.focusGhost') }
          ]}
        />
      </div>

      {result.clashes.length > 0 && (
        <GroupedVirtuoso
          style={{ height: RESULTS_LIST_HEIGHT }}
          groupCounts={panel.issueCounts}
          groupContent={(index) => (
            <IssueHeader>
              <span>{t('clash.issue', { number: index + 1 })}</span>
              <span>{panel.issueCounts[index]}</span>
            </IssueHeader>
          )}
          itemContent={(index) => {
            const clash = result.clashes[index];
            return (
              <ClashRow
                type="button"
                $active={clash.id === panel.activeClashId}
                onClick={() => panel.focus(clash)}
              >
                <div className="sides">
                  {sideLabel(clash.a)}
                  {sideLabel(clash.b)}
                  <div className="rule">{panel.nameOf(clash.rule)}</div>
                </div>
                <span className="distance">{distanceLabel(clash)}</span>
              </ClashRow>
            );
          }}
        />
      )}
    </Body>
  );

  return (
    // Mesma casca e posição do painel de propriedades; mais largo pelas listas.
    // Sem `destroyOnClose`: a configuração e o resultado são da sessão e não
    // somem ao fechar.
    <Drawer
      placement="right"
      closable={false}
      // Esc é do viewer (volta a cena ao padrão), não fecha o painel.
      keyboard={false}
      onClose={onClose}
      open={open}
      width={CLASH_DRAWER_WIDTH}
      height={DRAWER_HEIGHT}
      style={{
        position: 'absolute',
        height: DRAWER_HEIGHT,
        marginRight: 12,
        marginTop: 126,
        border: open ? '1px solid #EAEDF0' : 'none'
      }}
      className={stylesDrawer['drawer']}
      zIndex={2}
      mask={false}
      title={t('clash.title')}
      headerStyle={{ fontSize: '14px', fontWeight: 600, color: '#44566C', padding: '14px 20px' }}
    >
      <Content>
        <Tabs
          size="small"
          activeKey={panel.tab}
          onChange={(key) => panel.setTab(key as ClashTab)}
          items={[
            { key: 'run', label: t('clash.tabRun'), children: runTab },
            { key: 'rules', label: t('clash.tabRules'), children: rulesTab },
            {
              key: 'results',
              label: result ? t('clash.tabResultsCount', { count: result.clashes.length }) : t('clash.tabResults'),
              children: resultsTab
            }
          ]}
        />
      </Content>
    </Drawer>
  );
};

export default ClashPanel;
